'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  Ticket,
  Plus,
  Percent,
  DollarSign,
  Calendar,
  Copy,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  X
} from 'lucide-react';

interface VoucherItem {
  id: string;
  code: string;
  discountType: 'PERCENT' | 'FIXED';
  amount: number;
  description?: string;
  minSpend?: number;
  maxUses?: number;
  usedCount: number;
  expiresAt?: string;
  active: boolean;
}

export default function VouchersPage() {
  const [vouchers, setVouchers] = useState<VoucherItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Modal de Alta
  const [modalOpen, setModalOpen] = useState(false);
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'PERCENT' | 'FIXED'>('PERCENT');
  const [amount, setAmount] = useState<number>(20);
  const [description, setDescription] = useState('');
  const [minSpend, setMinSpend] = useState<number>(0);
  const [maxUses, setMaxUses] = useState<number>(100);
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/vouchers');
      const data = await res.json();
      if (data.success && Array.isArray(data.vouchers)) {
        setVouchers(data.vouchers);
      }
    } catch (e) {
      console.warn('Error fetching vouchers:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, []);

  const copyCode = (voucherCode: string) => {
    navigator.clipboard.writeText(voucherCode);
    setCopiedCode(voucherCode);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleCreateVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || amount <= 0) return;

    setSaving(true);
    try {
      const res = await fetch('/api/vouchers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim(),
          discountType,
          amount,
          description,
          minSpend,
          maxUses,
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null
        })
      });
      const data = await res.json();
      if (data.success) {
        setModalOpen(false);
        setCode('');
        setDescription('');
        fetchVouchers();
      } else {
        alert(data.error || 'Error al crear voucher');
      }
    } catch (err: any) {
      alert(err.message || 'Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Ticket className="w-6 h-6 text-emerald-600" />
            Vouchers y Cupones
          </h1>
          <p className="text-sm text-slate-500">
            Códigos de descuento reales persistidos en PostgreSQL para aplicar en reservas online y caja.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="px-4 py-2 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 self-start sm:self-auto cursor-pointer"
        >
          <Plus className="w-4 h-4" />
          <span>Crear Nuevo Voucher</span>
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-xs text-slate-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-300" />
          Cargando vouchers...
        </div>
      ) : vouchers.length === 0 ? (
        <div className="bg-white border border-slate-200/90 rounded-3xl p-8 text-center text-xs text-slate-500">
          No hay vouchers configurados actualmente. Creá tu primer cupón para tus clientes.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {vouchers.map((v) => (
            <div
              key={v.id}
              className="bg-white border border-slate-200/90 rounded-3xl p-6 shadow-sm flex flex-col justify-between space-y-4 relative overflow-hidden"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 font-bold text-xs border border-emerald-200">
                    {v.discountType === 'PERCENT' ? (
                      <>
                        <Percent className="w-3.5 h-3.5" />
                        {v.amount}% OFF
                      </>
                    ) : (
                      <>
                        <DollarSign className="w-3.5 h-3.5" />
                        ${v.amount.toLocaleString('es-AR')} OFF
                      </>
                    )}
                  </span>
                  <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {v.expiresAt
                      ? `Vence: ${new Date(v.expiresAt).toLocaleDateString('es-AR')}`
                      : 'Sin vencimiento'}
                  </span>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-black tracking-wider text-slate-900 font-mono">
                      {v.code}
                    </span>
                    <button
                      type="button"
                      onClick={() => copyCode(v.code)}
                      className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer"
                      title="Copiar código"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    {copiedCode === v.code && (
                      <span className="text-[10px] text-emerald-600 font-bold">¡Copiado!</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {v.description || 'Válido para servicios y productos del catálogo.'}
                  </p>
                  {v.minSpend && v.minSpend > 0 ? (
                    <span className="text-[11px] text-slate-400 block mt-1">
                      Gasto mínimo: ${v.minSpend.toLocaleString('es-AR')}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {v.usedCount} de {v.maxUses || '∞'} canjes utilizados
                </span>
                <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full text-[10px]">
                  {v.active ? 'Activo en base de datos' : 'Pausado'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Crear Voucher */}
      {modalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">Crear Nuevo Cupón</h3>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateVoucher} className="space-y-3.5 text-xs">
              <div className="space-y-1">
                <label className="font-bold text-slate-700 block">Código del Cupón</label>
                <input
                  type="text"
                  required
                  placeholder="EJ: VERANO2026"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono uppercase focus:bg-white focus:outline-emerald-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Tipo de Descuento</label>
                  <select
                    value={discountType}
                    onChange={(e: any) => setDiscountType(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-emerald-600"
                  >
                    <option value="PERCENT">Porcentaje (%)</option>
                    <option value="FIXED">Monto Fijo ($)</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Valor</label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono focus:bg-white focus:outline-emerald-600"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700 block">Descripción (opcional)</label>
                <input
                  type="text"
                  placeholder="Ej: Descuento especial de inauguración"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-emerald-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Gasto mínimo ($)</label>
                  <input
                    type="number"
                    min={0}
                    value={minSpend}
                    onChange={(e) => setMinSpend(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono focus:bg-white focus:outline-emerald-600"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-bold text-slate-700 block">Máximo de usos</label>
                  <input
                    type="number"
                    min={1}
                    value={maxUses}
                    onChange={(e) => setMaxUses(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-2xl border border-slate-200 bg-slate-50 font-mono focus:bg-white focus:outline-emerald-600"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700 block">Fecha de vencimiento (opcional)</label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full px-3.5 py-2 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-emerald-600"
                />
              </div>

              <div className="pt-2 flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-800"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold cursor-pointer disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Crear en PostgreSQL'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
